import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { getDownloadUrl } from 'service/project/api/media';

// A comment's saved video only stores { projectUuid, mediaUuid } — never a
// playable URL. The upload flow only ever returns a presigned S3 PUT url,
// and the matching GET url (from getDownloadUrl) is itself presigned and
// expires, so baking either into the saved HTML would leave the video
// broken once that expires. This resolves a fresh, short-lived view url on
// every mount instead — used both by the live Tiptap node view (while
// composing/editing) and by the read-only comment renderer (after
// posting), so playback works the same way in both places.
const urlCache = new Map<string, string>();

interface ResolvedVideoProps {
  projectUuid: string;
  mediaUuid: string;
  fileName?: string | null;
}

export const ResolvedVideo: React.FC<ResolvedVideoProps> = ({ projectUuid, mediaUuid, fileName }) => {
  const cacheKey = `${projectUuid}:${mediaUuid}`;
  const [url, setUrl] = useState<string | null>(() => urlCache.get(cacheKey) ?? null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(url ? 'ready' : 'loading');

  useEffect(() => {
    if (!projectUuid || !mediaUuid) {
      setStatus('error');
      return;
    }
    if (urlCache.has(cacheKey)) {
      setUrl(urlCache.get(cacheKey)!);
      setStatus('ready');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    getDownloadUrl(projectUuid, mediaUuid)
      .then((response) => {
        if (cancelled) return;
        const resolvedUrl = response?.downloadUrl || response?.url;
        if (resolvedUrl) {
          urlCache.set(cacheKey, resolvedUrl);
          setUrl(resolvedUrl);
          setStatus('ready');
        } else {
          setStatus('error');
        }
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectUuid, mediaUuid]);

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center gap-2 h-40 bg-primary-subtle/40 rounded-lg text-neutral-medium text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading video...
      </div>
    );
  }

  if (status === 'error' || !url) {
    return (
      <div className="flex items-center gap-2 h-16 bg-primary-subtle/40 rounded-lg px-4 text-red-400 text-sm">
        <AlertCircle className="w-4 h-4 shrink-0" />
        <span className="truncate">Video unavailable{fileName ? `: ${fileName}` : ''}</span>
      </div>
    );
  }

  return (
    <video controls className="max-w-full max-h-[400px] rounded-lg bg-black" src={url}>
      Your browser can&apos;t play this video format.
    </video>
  );
};

export default ResolvedVideo;
